import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

const configSchema = z.object({
  allocationId: z.string().min(1, 'Please select an allocation'),
  unitsPerBooking: z.number().int().min(1).default(1),
  isRequired: z.boolean().default(true),
  priority: z.number().int().min(0).default(0),
  autoConsume: z.boolean().default(true),
  dayOffset: z.number().int().default(0),
  notes: z.string().optional(),
});

type ConfigFormData = z.infer<typeof configSchema>;

type AllocationConfigType = {
  id: string;
  allocationId: string;
  unitsPerBooking: number;
  isRequired: boolean;
  priority: number;
  autoConsume: boolean;
  dayOffset: number;
  notes: string | null;
  allocation: {
    id: string;
    name: string;
    allocationType: string;
    totalUnits: number;
    consumedUnits: number;
    validFrom: Date;
    validTo: Date;
    availableUnits: number;
    partner: {
      id: string;
      companyName: string;
    };
  };
};

interface AllocationConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageId: string;
  config?: AllocationConfigType;
  existingAllocationIds: string[];
  onSuccess: () => void;
}

const allocationTypeLabels: Record<string, string> = {
  HOTEL_ROOM: 'Hotel Room',
  TOUR_SEAT: 'Tour Seat',
  TRANSFER_VEHICLE: 'Transfer',
  FLIGHT_SEAT: 'Flight Seat',
};

export function AllocationConfigModal({
  open,
  onOpenChange,
  packageId,
  config,
  existingAllocationIds,
  onSuccess,
}: AllocationConfigModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAllocation, setSelectedAllocation] = useState<{
    id: string;
    name: string;
    allocationType: string;
    availableUnits: number;
    partner: { companyName: string };
  } | null>(config?.allocation || null);

  const isEditing = !!config;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ConfigFormData>({
    defaultValues: {
      allocationId: config?.allocationId || '',
      unitsPerBooking: config?.unitsPerBooking || 1,
      isRequired: config?.isRequired ?? true,
      priority: config?.priority || 0,
      autoConsume: config?.autoConsume ?? true,
      dayOffset: config?.dayOffset || 0,
      notes: config?.notes || '',
    },
  });

  // Fetch available allocations for selection
  const { data: allocationsData, isLoading: isLoadingAllocations } = trpc.allocation.list.useQuery(
    {
      page: 1,
      search: searchQuery || undefined,
      status: 'ACTIVE',
      hasAvailability: true,
    },
    {
      enabled: open && !isEditing,
    }
  );

  // Filter out already linked allocations
  const availableAllocations =
    allocationsData?.allocations.filter((a) => !existingAllocationIds.includes(a.id)) || [];

  const createMutation = trpc.packageAllocationConfig.create.useMutation({
    onSuccess: () => {
      reset();
      setSelectedAllocation(null);
      setIsSubmitting(false);
      showSuccessToast('Allocation linked to package');
      onSuccess();
      onOpenChange(false);
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || 'Failed to link allocation');
    },
  });

  const updateMutation = trpc.packageAllocationConfig.update.useMutation({
    onSuccess: () => {
      setIsSubmitting(false);
      showSuccessToast('Configuration updated');
      onSuccess();
      onOpenChange(false);
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || 'Failed to update configuration');
    },
  });

  const onSubmit = (data: ConfigFormData) => {
    setIsSubmitting(true);

    if (isEditing && config) {
      updateMutation.mutate({
        id: config.id,
        unitsPerBooking: data.unitsPerBooking,
        isRequired: data.isRequired,
        priority: data.priority,
        autoConsume: data.autoConsume,
        dayOffset: data.dayOffset,
        notes: data.notes || null,
      });
    } else {
      createMutation.mutate({
        packageId,
        allocationId: data.allocationId,
        unitsPerBooking: data.unitsPerBooking,
        isRequired: data.isRequired,
        priority: data.priority,
        autoConsume: data.autoConsume,
        dayOffset: data.dayOffset,
        notes: data.notes,
      });
    }
  };

  const handleSelectAllocation = (allocation: (typeof availableAllocations)[0]) => {
    setSelectedAllocation(allocation);
    setValue('allocationId', allocation.id);
  };

  const isRequired = watch('isRequired');
  const autoConsume = watch('autoConsume');

  useEffect(() => {
    if (open && config) {
      reset({
        allocationId: config.allocationId,
        unitsPerBooking: config.unitsPerBooking,
        isRequired: config.isRequired,
        priority: config.priority,
        autoConsume: config.autoConsume,
        dayOffset: config.dayOffset,
        notes: config.notes || '',
      });
      setSelectedAllocation(config.allocation);
    } else if (!open) {
      reset({
        allocationId: '',
        unitsPerBooking: 1,
        isRequired: true,
        priority: 0,
        autoConsume: true,
        dayOffset: 0,
        notes: '',
      });
      setSelectedAllocation(null);
      setSearchQuery('');
    }
  }, [open, config, reset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Allocation Config' : 'Link Allocation'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the configuration for this allocation.'
              : 'Link a supplier allocation to this package.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Allocation Selection (only for create) */}
          {!isEditing && (
            <div className="space-y-2">
              <Label>Select Allocation *</Label>
              {selectedAllocation ? (
                <div className="rounded-lg border p-3 bg-muted/30">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{selectedAllocation.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {selectedAllocation.partner.companyName} •{' '}
                        {allocationTypeLabels[selectedAllocation.allocationType]}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {selectedAllocation.availableUnits} units available
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedAllocation(null);
                        setValue('allocationId', '');
                      }}
                    >
                      Change
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search allocations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto rounded-lg border">
                    {isLoadingAllocations ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    ) : availableAllocations.length === 0 ? (
                      <p className="text-center text-sm text-muted-foreground py-4">
                        No available allocations found
                      </p>
                    ) : (
                      availableAllocations.map((allocation) => (
                        <button
                          key={allocation.id}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors border-b last:border-b-0"
                          onClick={() => handleSelectAllocation(allocation)}
                        >
                          <p className="font-medium text-sm">{allocation.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {allocation.partner.companyName} •{' '}
                            {allocationTypeLabels[allocation.allocationType]} •{' '}
                            {allocation.availableUnits} available
                          </p>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
              {errors.allocationId && (
                <p className="text-xs text-red-500">{errors.allocationId.message}</p>
              )}
            </div>
          )}

          {/* Show selected allocation info when editing */}
          {isEditing && selectedAllocation && (
            <div className="rounded-lg border p-3 bg-muted/30">
              <p className="font-medium">{selectedAllocation.name}</p>
              <p className="text-sm text-muted-foreground">
                {selectedAllocation.partner.companyName} •{' '}
                {allocationTypeLabels[selectedAllocation.allocationType]}
              </p>
            </div>
          )}

          {/* Configuration Fields */}
          <div className="grid gap-4 grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="unitsPerBooking">Units per Booking</Label>
              <Input
                id="unitsPerBooking"
                type="number"
                min={1}
                {...register('unitsPerBooking', { valueAsNumber: true })}
              />
              <p className="text-xs text-muted-foreground">Units to consume per booking</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dayOffset">Day Offset</Label>
              <Input
                id="dayOffset"
                type="number"
                {...register('dayOffset', { valueAsNumber: true })}
              />
              <p className="text-xs text-muted-foreground">Days from travel start</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Required</Label>
                <p className="text-xs text-muted-foreground">Must have availability to book</p>
              </div>
              <Switch
                checked={isRequired}
                onCheckedChange={(checked) => setValue('isRequired', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Auto-consume</Label>
                <p className="text-xs text-muted-foreground">Automatically consume on booking</p>
              </div>
              <Switch
                checked={autoConsume}
                onCheckedChange={(checked) => setValue('autoConsume', checked)}
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Any notes about this allocation..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || (!isEditing && !selectedAllocation)}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Update' : 'Link Allocation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
